import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { type IIconProps } from 'shared/ui'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { AiCallingApi } from 'entities/AICalling/api/aiCallingApi'
import { type ICallingListResponse } from 'entities/AICalling/api/types'
import { type ISmartCallFlowAICallingAgentStep } from 'entities/SmartCall/api/types'
import { FlowActionStore } from 'widgets/Flow'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import {
  allSmartCallFlowActionTypes,
  type ISmartCallFlowActionType,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'
import {
  CallingTemplateContent,
  type ICallingTemplateContentProps,
} from '../ui/NewAICallingAgentModal/CallingTemplateContent'

export type IAICallingAgentStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  childId?: ISmartCallFlowActionStoreProps['childId']
  args?: ISmartCallFlowAICallingAgentStep['args']
}

export class AICallingAgentStore {
  static readonly type = 'aiCallingAgent' satisfies ISmartCallFlowActionType
  static title = 'AI Calling Agent'
  static iconProps: IIconProps = {
    icon: 'stars',
    color: 'var(--purple-70)',
  }

  public readonly actionStore: ISmartCallActionStore

  constructor(
    private _entities: IEntitiesStore,
    { args, id, ...props }: IAICallingAgentStoreProps
  ) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: AICallingAgentStore.type,
      iconProps: AICallingAgentStore.iconProps,
      name: AICallingAgentStore.title,
      allowedBeforeActions: allSmartCallFlowActionTypes.filter((type) => type !== 'aiCallingAgent'),
      allowedNextActions: ['sendMessage', 'aiTextingAgent'],
    })

    if (args) {
      this.initByArgs(args)
    }

    makeAutoObservable(this)
  }

  get args(): ISmartCallFlowAICallingAgentStep['args'] {
    return {
      agent_id: this.aiCallingAgent?.id ?? null,
    }
  }

  private _stepIdType: Pick<ISmartCallFlowAICallingAgentStep, 'id' | 'type'> = {
    id: 'aiCallingAgent',
    type: 'action',
  }

  get step(): ISmartCallFlowAICallingAgentStep {
    return {
      ...this._stepIdType,
      args: this.args,
      next: this.actionStore.nextId,
    }
  }

  initByArgs = async (args: ISmartCallFlowAICallingAgentStep['args']) => {
    try {
      if (args?.agent_id) {
        const { data } = await AiCallingApi.getById(args.agent_id)
        this.setAiCallingAgent(data)
      }
    } catch (error) {
      logger.error(error)
    }
  }

  validate = () => {
    if (!this.args?.agent_id) {
      this.setAiCallingAgentError('Required field')
      return false
    }
    this.setAiCallingAgentError('')
    return true
  }

  get isConfigurationError() {
    return !!this.aiCallingAgentError
  }

  setApiError = (key: string, message: string) => {
    if (key.includes('agent_id')) {
      this.setAiCallingAgentError(message)
    }
  }

  private _aiCallingAgentError = ''
  get aiCallingAgentError() {
    return this._aiCallingAgentError
  }
  setAiCallingAgentError = (value: string) => {
    this._aiCallingAgentError = value
  }

  openNewAgentModal = (closeDropdown: () => void) => {
    closeDropdown()
    const modalId = nanoid()
    modalStore.addModal<ICallingTemplateContentProps>({
      id: modalId,
      title: 'Choose template',
      pureContent: true,
      ModalContent: CallingTemplateContent,
      ModalContentProps: { modalId, onCreated: this.setAiCallingAgent },
      width: 798,
    })
  }

  private _aiCallingAgent: ICallingListResponse | null = null
  get aiCallingAgent() {
    return this._aiCallingAgent
  }
  setAiCallingAgent = (aiAgent: typeof this._aiCallingAgent) => {
    this._aiCallingAgentError = ''
    this._aiCallingAgent = aiAgent
  }
}
